import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BuildIcon from '@mui/icons-material/Build';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.72rem', textTransform: 'uppercase',
  backgroundColor: '#2d3748',
  borderRight: '1px solid rgba(255,255,255,0.15)', py: 1.0, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 0.8, px: 0.8, fontSize: '0.76rem', textAlign: 'center' };

function fmtDate(val) {
  if (!val) return '—';
  const s = String(val).substring(0, 10);
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr}`;
}

const COLS = [
  'SR', 'DATE', 'TIME', 'SHIFT', 'MACHINE', 'MECHANIC',
  'PROBLEM', 'CORRECTION', 'PARTS USED', 'REMARKS', 'DURATION', 'STATUS'
];

const DailyBreakdownReportView = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [shift, setShift]       = useState('BOTH');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [rows, setRows]         = useState(null);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both dates'); return; }
    setLoading(true); setError(''); setRows(null);
    try {
      const res = await fetch(`${API_URL}/order/breakdownreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
          shift,
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data) throw new Error('No data returned');
      setRows(apiData.data[0] || []);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!rows?.length) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Breakdown Report');
    XLSX.writeFile(wb, `BreakdownReport_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`);
  };

  // Summary counts
  const totalSolved   = rows?.filter(r => r.SOLVE_STATUS_VALUE === 1 || String(r.SOLVE_STATUS || '').toUpperCase() === 'SOLVED').length || 0;
  const totalPending  = rows?.filter(r => r.SOLVE_STATUS_VALUE !== 1 && String(r.SOLVE_STATUS || '').toUpperCase() !== 'SOLVED').length || 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Search card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}><ArrowBackIcon /></IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <BuildIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#7f1d1d' }}>Daily Breakdown Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Machine breakdown history by date range
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center" flexWrap="wrap">
              <DatePicker label="From Date" value={fromDate} onChange={setFromDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
              <DatePicker label="To Date" value={toDate} onChange={setToDate} minDate={fromDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
              <ToggleButtonGroup
                value={shift} exclusive
                onChange={(_, v) => { if (v) setShift(v); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2 },
                  '& .Mui-selected': { backgroundColor: '#7f1d1d !important', color: '#fff !important' },
                }}
              >
                <ToggleButton value="DAY">DAY</ToggleButton>
                <ToggleButton value="NIGHT">NIGHT</ToggleButton>
                <ToggleButton value="BOTH">BOTH</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{ minWidth: 130, backgroundColor: '#7f1d1d', '&:hover': { backgroundColor: '#450a0a' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: '#7f1d1d' }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows && (
        <>
          {/* Summary strip */}
          {rows.length > 0 && (
            <Card sx={{
              mb: 3, borderRadius: 2,
              background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box display="flex" flexWrap="wrap" gap={4} alignItems="center">
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Total Breakdowns
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {rows.length}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Solved
                    </Typography>
                    <Typography sx={{ color: '#a7f3d0', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {totalSolved}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Pending
                    </Typography>
                    <Typography sx={{ color: '#fef08a', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {totalPending}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              disabled={!rows.length}
              sx={{ color: '#7f1d1d', fontWeight: 600, textTransform: 'none', border: '1px solid #7f1d1d40', borderRadius: 1, px: 2 }}>
              Download Excel
            </Button>
            <Button size="small" variant="outlined"
              disabled={!rows.length}
              onClick={() => {
                const url = `${API_URL}/api/breakdown/preview?from_date=${fromDate.format('YYYY-MM-DD')}&to_date=${toDate.format('YYYY-MM-DD')}&shift=${shift}`;
                window.open(url, '_blank');
              }}
              sx={{ color: '#7f1d1d', fontWeight: 600, textTransform: 'none', border: '1px solid #7f1d1d40', borderRadius: 1, px: 2 }}>
              Preview Image
            </Button>
          </Box>

          {rows.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
              <BuildIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#666' }}>No breakdowns found</Typography>
              <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>No machine breakdowns recorded in this date range</Typography>
            </Card>
          ) : (
            <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <Box sx={{ background: '#7f1d1d', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>
                  Machine Breakdown Records
                </Typography>
                <Box sx={{ background: 'rgba(255,255,255,0.18)', px: 1.2, py: 0.2, borderRadius: 1 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
                    {rows.length} records
                  </Typography>
                </Box>
              </Box>

              <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      {COLS.map(h => <TableCell key={h} sx={HDR_SX}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => {
                      const solved = row.SOLVE_STATUS_VALUE === 1 || String(row.SOLVE_STATUS || '').toUpperCase() === 'SOLVED';
                      return (
                        <TableRow key={i} sx={{
                          backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                          '&:hover': { backgroundColor: '#fef2f2' },
                          borderLeft: solved ? '3px solid #16a34a' : '3px solid #dc2626',
                        }}>
                          <TableCell sx={CELL_SX}>{row.SRNO}</TableCell>
                          <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{fmtDate(row.BREAKDOWN_DATE)}</TableCell>
                          <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.BREAKDOWN_TIME || '—'}</TableCell>
                          <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: row.SHIFT === 'NIGHT' ? '#1565c0' : '#166534' }}>
                            {row.SHIFT || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: '#7f1d1d', textAlign: 'left', px: 1, minWidth: 160 }}>
                            {row.MACHINENAME || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, color: '#374151', px: 1 }}>{row.MECHANIC || '—'}</TableCell>
                          <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1, minWidth: 180, maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {row.PROBLEMLIST || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1, minWidth: 180, maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word', color: '#166534' }}>
                            {row.CORRECTIONLIST || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1, minWidth: 140, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word', color: '#666' }}>
                            {row.PARTS_UTILIZE || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1, minWidth: 120, maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', color: '#888' }}>
                            {row.REMARKS || '—'}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, color: '#374151' }}>{row.WORKING_DURATION || '—'}</TableCell>
                          <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.8 }}>
                            {solved
                              ? <Chip label="SOLVED" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                              : <Chip label="PENDING" size="small" sx={{ backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}

      {!rows && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
          <BuildIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>No data to display</Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>Select a date range and click Search</Typography>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Breakdown Report to WhatsApp"
            apiUrl={fromDate && toDate ? `${API_URL}/api/breakdown/send` : null}
            payload={{ from_date: fromDate?.format('YYYY-MM-DD'), to_date: toDate?.format('YYYY-MM-DD'), shift }}
            disabled={!rows.length}
          />
        </Box>
      )}
    </Container>
  );
};

export default DailyBreakdownReportView;
